import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { LocationProvider } from "./contexts/LocationContext";
import { SocketProvider } from "./contexts/SocketContext";
import ClerkProviderWrapper from "./contexts/ClerkProvider";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { Provider } from "react-redux";
import store from "./store";
import AOS from "aos";
import { ReactLenis } from "lenis/react";
import useScrollToTop from "./hooks/useScrollToTop";

// Import CSS
import "bootstrap/dist/css/bootstrap.min.css";
import "react-toastify/dist/ReactToastify.css";
import "aos/dist/aos.css";
import "./styles/global-enhancements.css";
import "./styles/modern-notifications.css";

// Components
import CustomNavbar from "./components/common/CustomNavbar";
import ModernLoader from "./components/common/ModernLoader";
import BrandLogo from "./components/common/BrandLogo";
import ClerkProtectedRoute from "./components/common/ClerkProtectedRoute";
import Footer from "./components/Footer";

// Pages
import Home from "./pages/Home";
import About from "./pages/About";
import ClerkSignIn from "./pages/ClerkSignIn";
import ClerkSignUp from "./pages/ClerkSignUp";
import ClerkUserProfile from "./pages/ClerkUserProfile";
import Movies from "./pages/Movies";
import MovieDetails from "./pages/MovieDetails";
import ShowTimes from "./pages/ShowTimes";
import ShowtimePage from "./pages/ShowtimePage";
import BookingPage from "./pages/BookingPage";
import BookingProcessing from "./pages/BookingProcessing";
import MyBookings from "./pages/MyBookings";
import BookingConfirmation from "./pages/BookingConfirmation";
import Theaters from "./pages/Theaters";
import PaymentTest from "./components/payment/PaymentTest";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminMovies from "./pages/admin/AdminMovies";
import AdminTheaters from "./pages/admin/AdminTheaters";
import AdminBookings from "./pages/admin/AdminBookings";
import AdminShows from "./pages/admin/AdminShows";
import AdminUsers from "./pages/admin/AdminUsers";
import DataSync from "./pages/admin/DataSync";
import TMDBPreview from "./pages/admin/TMDBPreview";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminSubscriptions from "./pages/admin/AdminSubscriptions";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminThemeProvider from "./components/admin/AdminThemeProvider";
import AdminLayout from "./components/admin/AdminLayout";
import SeatLayoutBuilder from "./components/admin/SeatLayoutBuilder";

// Partner Pages
import PartnerDashboard from "./pages/partner/PartnerDashboard";
import PartnerOnboarding from "./pages/partner/PartnerOnboarding";
import PartnerScreens from "./pages/partner/PartnerScreens";
import PartnerShows from "./pages/partner/PartnerShows";
import PartnerBookings from "./pages/partner/PartnerBookings";
import PartnerAnalytics from "./pages/partner/PartnerAnalytics";
import PartnerScanner from "./pages/partner/PartnerScanner";
import PartnerSettings from "./pages/partner/PartnerSettings";
import PartnerSubscription from "./pages/partner/PartnerSubscription";
import PartnerTheaters from "./pages/partner/PartnerTheaters";
import PartnerLayout from "./components/partner/PartnerLayout";

const AppPaddingStyles = () => (
  <style>
    {`
      @media (max-width: 768px) {
        .App {
          padding-bottom: 100px !important;
        }
      }
    `}
  </style>
);

const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();

  if (!user) return <Navigate to="/login" />;

  if (adminOnly && user.role !== "admin") {
    return <Navigate to="/" />;
  }

  return children;
};

const AppContent = () => {
  const location = useLocation();
  const { user, loading } = useAuth();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPartnerRoute = location.pathname.startsWith("/partner");
  const isPartnerOnboarding = location.pathname === "/partner/onboarding";
  const isAuthRoute =
    location.pathname.startsWith("/sign-in") ||
    location.pathname.startsWith("/sign-up");
  const isBookingFlowRoute =
    location.pathname.startsWith("/booking/") ||
    location.pathname.startsWith("/booking-processing");

  useScrollToTop();

  return (
    <>
      {!isAdminRoute && (!isPartnerRoute || isPartnerOnboarding) && !isAuthRoute && !isBookingFlowRoute && <CustomNavbar />}
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/about" element={<About />} />
        <Route path="/sign-in/*" element={<ClerkSignIn />} />
        <Route path="/sign-up/*" element={<ClerkSignUp />} />

        <Route path="/clerk-profile/*" element={<ClerkUserProfile />} />
        <Route path="/movies" element={<Movies />} />
        <Route path="/theaters" element={<Theaters />} />
        <Route path="/movies/:id" element={<MovieDetails />} />
        <Route path="/movie/:id" element={<MovieDetails />} />
        <Route path="/movie/:movieId/showtimes" element={<ShowTimes />} />
        <Route path="/showtimes/:movieId" element={<ShowtimePage />} />

        <Route path="/booking/:showId" element={<BookingPage />} />

        <Route
          path="/booking-processing/:bookingId"
          element={
            <ClerkProtectedRoute>
              <BookingProcessing />
            </ClerkProtectedRoute>
          }
        />

        <Route
          path="/my-bookings"
          element={
            <ClerkProtectedRoute>
              <MyBookings />
            </ClerkProtectedRoute>
          }
        />

        <Route
          path="/booking-confirmation/:bookingId"
          element={
            <ClerkProtectedRoute>
              <BookingConfirmation />
            </ClerkProtectedRoute>
          }
        />

        {/* Partner Routes */}
        <Route
          path="/partner/onboarding"
          element={
            <ClerkProtectedRoute>
              <AdminThemeProvider>
                <PartnerOnboarding />
              </AdminThemeProvider>
            </ClerkProtectedRoute>
          }
        />

        <Route
          path="/partner/*"
          element={
            <ClerkProtectedRoute partnerOnly>
              <AdminThemeProvider>
                <PartnerLayout>
                  <Routes>
                    <Route path="/" element={<PartnerDashboard />} />
                    <Route path="/screens" element={<PartnerScreens />} />
                    <Route path="/screens/:screenId/layout" element={<SeatLayoutBuilder />} />
                    <Route path="/shows" element={<PartnerShows />} />
                    <Route path="/theaters" element={<PartnerTheaters />} />
                    <Route path="/bookings" element={<PartnerBookings />} />
                    <Route path="/analytics" element={<PartnerAnalytics />} />
                    <Route path="/scanner" element={<PartnerScanner />} />
                    <Route path="/subscription" element={<PartnerSubscription />} />
                    <Route path="/settings" element={<PartnerSettings />} />
                  </Routes>
                </PartnerLayout>
              </AdminThemeProvider>
            </ClerkProtectedRoute>
          }
        />

        {/* Admin Routes with Centralized Theme Provider */}
        <Route
          path="/admin/*"
          element={
            <ClerkProtectedRoute adminOnly>
              <AdminThemeProvider>
                <AdminLayout>
                  <Routes>
                    <Route path="/" element={<AdminDashboard />} />
                    <Route path="/movies" element={<AdminMovies />} />
                    <Route path="/tmdb" element={<TMDBPreview />} />
                    <Route path="/theaters" element={<AdminTheaters />} />
                    <Route path="/theaters/:theaterId/screens/:screenId/layout" element={<SeatLayoutBuilder />} />
                    <Route path="/bookings" element={<AdminBookings />} />
                    <Route path="/shows" element={<AdminShows />} />
                    <Route path="/users" element={<AdminUsers />} />
                    <Route path="/partners" element={<AdminPartners />} />
                    <Route path="/subscriptions" element={<AdminSubscriptions />} />
                    <Route path="/notifications" element={<AdminNotifications />} />
                    <Route path="/data-sync" element={<DataSync />} />
                  </Routes>
                </AdminLayout>
              </AdminThemeProvider>
            </ClerkProtectedRoute>
          }
        />

        {/* Payment Test Route - Remove in production */}
        <Route path="/payment-test" element={<PaymentTest />} />
      </Routes>
      {!isAdminRoute && !isPartnerRoute && !isAuthRoute && !isBookingFlowRoute && <Footer />}
    </>
  );
};

const AppContentWrapper = () => {
  const location = useLocation();
  const isAdminRoute = location.pathname.startsWith("/admin");
  const isPartnerRoute = location.pathname.startsWith("/partner");

  if (isAdminRoute || isPartnerRoute) {
    return (
      <div className="App">
        <AppContent />
      </div>
    );
  }

  return (
    <ReactLenis
      root
      options={{
        duration: 1.0,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smooth: true,
        mouseMultiplier: 0.8,
        smoothTouch: false,
        touchMultiplier: 1.5,
        lerp: 0.12,
        normalizeWheel: true,
      }}
    >
      <div className="App">
        <AppPaddingStyles />
        <AppContent />
      </div>
    </ReactLenis>
  );
};

function App() {
  const [showBrandAnimation, setShowBrandAnimation] = useState(() => {
    return !sessionStorage.getItem("brandAnimationShown");
  });

  const handleBrandComplete = () => {
    sessionStorage.setItem("brandAnimationShown", "true");
    setShowBrandAnimation(false);
  };

  useEffect(() => {
    if (showBrandAnimation) {
      const timer = setTimeout(() => {
        handleBrandComplete();
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showBrandAnimation]);

  useEffect(() => {
    const handleBrowserExtError = (e) => {
      const msg = e.message || (e.reason && e.reason.message) || "";
      if (
        msg.includes("message channel closed") ||
        msg.includes("listener indicated an asynchronous response")
      ) {
        if (e.preventDefault) e.preventDefault();
        return true;
      }
      return false;
    };

    window.addEventListener("error", handleBrowserExtError);
    window.addEventListener("unhandledrejection", handleBrowserExtError);

    AOS.init({
      duration: 800,
      once: true,
      easing: "ease-out-quart",
      offset: 50,
      delay: 0,
      anchorPlacement: "top-bottom",
    });

    return () => {
      window.removeEventListener("error", handleBrowserExtError);
      window.removeEventListener("unhandledrejection", handleBrowserExtError);
    };
  }, []);

  if (showBrandAnimation) {
    return (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
        style={{
          background: "linear-gradient(180deg, #e2f1fe 0%, #ffffff 100%)",
          zIndex: 10000,
        }}
        onClick={handleBrandComplete}
      >
        <div style={{ transform: "scale(2)" }}>
          <BrandLogo />
        </div>
      </div>
    );
  }

  return (
    <Provider store={store}>
      <ClerkProviderWrapper>
        <AuthProvider>
          <LocationProvider>
            <SocketProvider>
              <Router
                future={{
                  v7_startTransition: true,
                  v7_relativeSplatPath: true,
                }}
              >
                <AppContentWrapper />
                <ToastContainer
                  position="top-center"
                  autoClose={1500}
                  hideProgressBar={true}
                  newestOnTop={true}
                  closeOnClick={true}
                  rtl={false}
                  pauseOnFocusLoss={false}
                  draggable={true}
                  pauseOnHover={false}
                  theme="light"
                  limit={3}
                />
              </Router>
            </SocketProvider>
          </LocationProvider>
        </AuthProvider>
      </ClerkProviderWrapper>
    </Provider>
  );
}

export default App;
